import { useState, MouseEvent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useIntl } from 'react-intl';

// material-ui
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';

// project-imports
import useConfig from 'hooks/useConfig';

// types
import { I18n } from 'types/config';
import { ThemeMode } from 'config';

// assets
import { Cpu, I24Support, Lock1, LanguageSquare, Sun1, Moon } from 'iconsax-reactjs';

// ==============================|| HEADER PROFILE - SETTING TAB ||============================== //

export default function SettingTab() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const intl = useIntl();
  const { i18n, onChangeLocalization, mode, onChangeMode } = useConfig();

  const [selectedIndex, setSelectedIndex] = useState<number>();
  const handleListItemClick = (_event: MouseEvent<HTMLDivElement>, index: number, route: string = '') => {
    setSelectedIndex(index);
    if (route && route !== '') {
      navigate(route);
    }
  };

  useEffect(() => {
    const pathToIndex: { [key: string]: number } = {
      '/ai-training': 0
    };

    setSelectedIndex(pathToIndex[pathname] ?? undefined);
  }, [pathname]);

  const handleLanguageToggle = () => {
    onChangeLocalization(i18n === 'pt-BR' ? 'en' : ('pt-BR' as I18n));
  };

  const handleThemeToggle = () => {
    onChangeMode(mode === ThemeMode.DARK ? ThemeMode.LIGHT : ThemeMode.DARK);
  };

  return (
    <List component="nav" sx={{ p: 0, '& .MuiListItemIcon-root': { minWidth: 32 } }}>
      <ListItemButton
        selected={selectedIndex === 0}
        onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 0, '/ai-training')}
      >
        <ListItemIcon>
          <Cpu variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={intl.formatMessage({ id: 'settings.aiTraining' })} />
      </ListItemButton>
      <ListItemButton selected={selectedIndex === 1} onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 1)}>
        <ListItemIcon>
          <I24Support variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={intl.formatMessage({ id: 'settings.support' })} />
      </ListItemButton>
      <ListItemButton selected={selectedIndex === 2} onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 2)}>
        <ListItemIcon>
          <Lock1 variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={intl.formatMessage({ id: 'settings.privacyCenter' })} />
      </ListItemButton>

      <Divider sx={{ my: 1 }} />

      <ListSubheader sx={{ bgcolor: 'transparent', p: 0, pl: 1, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {intl.formatMessage({ id: 'settings.preferences' })}
        </Typography>
      </ListSubheader>

      <ListItemButton onClick={handleLanguageToggle}>
        <ListItemIcon>
          <LanguageSquare variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText
          primary={intl.formatMessage({ id: 'settings.language' })}
          secondary={i18n === 'pt-BR' ? 'Português (Brasil)' : 'English (US)'}
        />
      </ListItemButton>

      <ListItemButton onClick={handleThemeToggle}>
        <ListItemIcon>{mode === ThemeMode.DARK ? <Sun1 variant="Bulk" size={18} /> : <Moon variant="Bulk" size={18} />}</ListItemIcon>
        <ListItemText
          primary={intl.formatMessage({ id: 'settings.theme' })}
          secondary={
            mode === ThemeMode.DARK ? intl.formatMessage({ id: 'settings.themeLight' }) : intl.formatMessage({ id: 'settings.themeDark' })
          }
        />
      </ListItemButton>
    </List>
  );
}
